/**
 * Live-traffic particle overlay — a stream of blue dots flowing along each
 * board edge, denser and brighter the more requests that edge is currently
 * serving. Renders INSIDE a space's edge SVG, so it inherits the exact edge
 * geometry and the board's zoom/pan transform (perfect alignment, crisp at any
 * zoom, no projection math).
 *
 * The motion model lives in `particleFlow.ts` (pure, tested). This component
 * is the thin renderer: it measures each edge's path once (via a detached
 * SVGPathElement), runs a requestAnimationFrame loop stepping the engine, and
 * positions a pool of <circle> DOM nodes imperatively — React never re-renders
 * per frame.
 *
 * Live-only paint: this layer never participates in scene export (traffic is a
 * property of the running system, not the diagram) and no-ops where SVG path
 * measurement is unavailable (SSR / headless / jsdom).
 */

import { memo, useEffect, useMemo, useRef } from "react";
import type { BoardEdge } from "@archlang/scene/board";
import type { PlannedRoute } from "@archlang/scene/board";
import { createFlowState, stepFlow, type FlowEdgeInput } from "./particleFlow.js";
import { useLiveEdgeRates, trafficEdgeKey } from "./liveTrafficStore.js";
import { edgeServicePair } from "./edgeServicePair.js";
import { useFlowVisible } from "./telemetry/flowVisibilityStore.js";

const SVGNS = "http://www.w3.org/2000/svg";

/** Base dot radius in space units; scaled up slightly on busier edges. */
const DOT_RADIUS = 3.2;
/** Rate (particles/sec) that reads as "fully saturated" — caps the visual
 *  intensity ramp so one hot edge doesn't blow out. */
const SATURATION_RATE = 24;

export interface ParticleFlowLayerProps {
  readonly edges: readonly BoardEdge[];
  readonly routes: ReadonlyMap<string, PlannedRoute>;
}

/** Arc-length-uniform samples per edge path. getPointAtLength forces an SVG
 *  geometry layout, so calling it per-particle per-frame (dozens × 60fps) was a
 *  hot reflow source. Sample the path ONCE into this table and lerp per frame. */
const PATH_SAMPLES = 128;

interface MeasuredEdge {
  readonly input: FlowEdgeInput;
  /** Flat [x0,y0,x1,y1,…], length (PATH_SAMPLES+1)*2, uniform by arc length. */
  readonly samples: Float64Array;
  /** 0..1 intensity for this edge, for per-dot size/brightness. */
  readonly intensity: number;
}

/** Detached path measurement — works without mounting the path in the DOM in
 *  real browsers; returns null where the SVG geometry API is absent. */
function measurePath(d: string): SVGPathElement | null {
  if (typeof document === "undefined") return null;
  const path = document.createElementNS(SVGNS, "path");
  path.setAttribute("d", d);
  if (typeof path.getTotalLength !== "function") return null;
  return path;
}

/** Lerp a pre-sampled path at fraction t∈[0,1] — pure arithmetic, no layout. */
export function sampleAt(samples: Float64Array, t: number): [number, number] {
  const n = samples.length / 2 - 1; // == PATH_SAMPLES
  const f = Math.min(1, Math.max(0, t)) * n;
  const i0 = Math.min(n - 1, Math.floor(f));
  const frac = f - i0;
  const ax = samples[i0 * 2];
  const ay = samples[i0 * 2 + 1];
  const bx = samples[(i0 + 1) * 2];
  const by = samples[(i0 + 1) * 2 + 1];
  return [ax + (bx - ax) * frac, ay + (by - ay) * frac];
}

function ParticleFlowLayerImpl({ edges, routes }: ParticleFlowLayerProps) {
  const gRef = useRef<SVGGElement | null>(null);
  const stateRef = useRef(createFlowState());
  const rates = useLiveEdgeRates();

  // Rebuild the measured, active edge set only when geometry or rates change —
  // NOT per frame. An edge is active only while it carries live traffic.
  const measured = useMemo<MeasuredEdge[]>(() => {
    const out: MeasuredEdge[] = [];
    for (const e of edges) {
      const pair = edgeServicePair(e);
      if (!pair) continue;
      const rate = rates.get(trafficEdgeKey(pair[0], pair[1])) ?? 0;
      if (rate <= 0) continue;
      const route = routes.get(e.id);
      if (!route) continue;
      const path = measurePath(route.d);
      if (!path) continue;
      let length = 0;
      try {
        length = path.getTotalLength();
      } catch {
        continue;
      }
      if (!(length > 0)) continue;
      // Sample the path once here (uniform by arc length); the frame loop lerps
      // this table instead of calling getPointAtLength per particle per frame.
      const samples = new Float64Array((PATH_SAMPLES + 1) * 2);
      let sampled = true;
      for (let i = 0; i <= PATH_SAMPLES; i++) {
        try {
          const pt = path.getPointAtLength((i / PATH_SAMPLES) * length);
          samples[i * 2] = pt.x;
          samples[i * 2 + 1] = pt.y;
        } catch {
          sampled = false;
          break;
        }
      }
      if (!sampled) continue;
      const intensity = Math.min(1, rate / SATURATION_RATE);
      out.push({ input: { id: e.id, rate, length }, samples, intensity });
    }
    return out;
  }, [edges, routes, rates]);

  useEffect(() => {
    const g = gRef.current;
    if (!g) return;
    const inputs = measured.map((m) => m.input);
    const samplesById = new Map(measured.map((m) => [m.input.id, m.samples]));
    const intensityById = new Map(measured.map((m) => [m.input.id, m.intensity]));

    // Nothing flowing → clear any leftover dots and idle.
    if (inputs.length === 0) {
      while (g.firstChild) g.removeChild(g.firstChild);
      stateRef.current = createFlowState();
      return;
    }

    const pool: SVGCircleElement[] = [];
    let raf = 0;
    let last = 0;

    const frame = (now: number) => {
      const dt = last === 0 ? 0 : (now - last) / 1000;
      last = now;
      const state = stepFlow(stateRef.current, dt, inputs);

      // Ensure the pool is large enough; grow lazily.
      while (pool.length < state.particles.length) {
        const c = document.createElementNS(SVGNS, "circle");
        c.setAttribute("class", "flow-dot");
        g.appendChild(c);
        pool.push(c);
      }

      for (let i = 0; i < state.particles.length; i++) {
        const p = state.particles[i];
        const samples = samplesById.get(p.edgeId);
        const c = pool[i];
        if (!samples) {
          c.style.display = "none";
          continue;
        }
        const [px, py] = sampleAt(samples, p.t);
        const intensity = intensityById.get(p.edgeId) ?? 0.5;
        // Fade in from the source and out toward the target so dots don't pop.
        const edgeFade = Math.min(1, p.t * 6, (1 - p.t) * 6);
        const r = DOT_RADIUS * (0.75 + 0.5 * intensity);
        c.setAttribute("cx", String(px));
        c.setAttribute("cy", String(py));
        c.setAttribute("r", String(r));
        c.style.display = "";
        c.style.opacity = String((0.35 + 0.55 * intensity) * edgeFade);
      }
      // Park unused pool nodes.
      for (let i = state.particles.length; i < pool.length; i++) {
        pool[i].style.display = "none";
      }

      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      while (g.firstChild) g.removeChild(g.firstChild);
    };
  }, [measured]);

  return <g ref={gRef} className="flow-layer" style={{ pointerEvents: "none" }} />;
}

const ParticleFlowLayerMemo = memo(ParticleFlowLayerImpl);

/** Gated on the "Live" toggle (off by default): the animation is opt-in, so the
 *  board stays calm until the user turns it on. When off, nothing mounts — the
 *  rAF loop never starts. */
export function ParticleFlowLayer(props: ParticleFlowLayerProps) {
  const visible = useFlowVisible();
  if (!visible) return null;
  return <ParticleFlowLayerMemo {...props} />;
}
